import ChatIcon from "@mui/icons-material/Chat";
import { Badge, IconButton, IconButtonProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { FeatureFlagName } from "../../app/featureFlags/featureFlags";
import { canViewFeature } from "../../app/featureFlags/helpers";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { canExpertUseMessaging } from "../user/accessRightChecks";
import {
  selectFloatingChatButtonBottom,
  selectHasUnreadMessages,
  selectIsFloatingWindowOpen,
  selectMessagingServiceUnavailable,
  setFloatingWindowOpen,
} from "./messagingSlice";
import { FloatingChatButtonContainer } from "./styles";
import { isMessagingPagePath } from "./useMessagingViewMode";

const FloatingChatButton = (props: Omit<IconButtonProps, "onClick">) => {
  const { t } = useTranslation("messaging");
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.user.user);
  const isOpen = useAppSelector(selectIsFloatingWindowOpen);
  const hasUnreadMessages = useAppSelector(selectHasUnreadMessages);
  const bottom = useAppSelector(selectFloatingChatButtonBottom);
  const serviceUnavailable = useAppSelector(selectMessagingServiceUnavailable);

  if (
    serviceUnavailable ||
    !canViewFeature(FeatureFlagName.MESSAGING, user) ||
    !canExpertUseMessaging(user) ||
    isOpen ||
    isMessagingPagePath(pathname)
  ) {
    return null;
  }

  return (
    <FloatingChatButtonContainer sx={{ bottom }}>
      <Badge
        variant="dot"
        color="warning"
        overlap="circular"
        invisible={!hasUnreadMessages}
      >
        <IconButton
          aria-label={t("messaging")}
          data-testid="floating-chat-button"
          onClick={() => dispatch(setFloatingWindowOpen(true))}
          {...props}
          sx={{
            width: 56,
            height: 56,
            bgcolor: "primary.main",
            boxShadow: 6,
            "&:hover": {
              bgcolor: "hover.primaryLight",
            },
            ...props.sx,
          }}
        >
          <ChatIcon width={32} height={32} sx={{ color: "white" }} />
        </IconButton>
      </Badge>
    </FloatingChatButtonContainer>
  );
};

export default FloatingChatButton;
